import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { ArrowRight } from 'lucide-react';

const ROWS = [
  ['Cost', 'Flat monthly plan — unlimited', 'Drone + pilot time per job', 'Free, but your labor'],
  ['Speed', '~60 seconds', 'Travel + flight + processing', 'Slowest (on-site, by hand)'],
  ['Site visit', 'None — from your desk', 'Required (fly on-site)', 'Required (climb/measure)'],
  ['Equipment', 'None', 'Drone + FAA Part 107 license', 'Ladder, tape, wheel'],
  ['Safety', 'No roof access', 'No climbing, but flight risk', 'Fall risk'],
  ['Best for', 'Fast estimates at scale', 'Detailed visuals / inspection', 'One-off, no tools'],
];

export default function DroneVsSatellite() {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();
  const handleStart = () => (isAuthenticated ? navigate('/dashboard') : openAuthModal('signup'));

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Drone vs Satellite Roof Measurement: Which Is Better for Contractors?</title>
        <meta name="description" content="Drone vs satellite roof measurement compared for contractors: cost, speed, equipment, licensing, and safety. See why many roofers use satellite measurement for fast estimates — no drone, pilot license, or site visit required." />
        <meta property="og:title" content="Drone vs Satellite Roof Measurement (2026)" />
        <meta property="og:description" content="Cost, speed, equipment, and safety compared — and when satellite beats drone for roof estimates." />
        <link rel="canonical" href="https://squarecount.io/drone-vs-satellite-roof-measurement" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'Is satellite or drone better for roof measurement?', acceptedAnswer: { '@type': 'Answer', text: 'For fast, low-cost estimates at scale, satellite roof measurement is usually better — it takes about 60 seconds from your desk with no drone, pilot license, or site visit. Drones are better when you need detailed close-up visuals or a physical inspection of the roof surface.' } },
            { '@type': 'Question', name: 'Do I need a license to measure roofs with a drone?', acceptedAnswer: { '@type': 'Answer', text: 'In the US, flying a drone commercially requires an FAA Part 107 Remote Pilot Certificate. Satellite roof measurement tools like SquareCount require no license, equipment, or on-site visit.' } },
            { '@type': 'Question', name: 'How accurate is satellite roof measurement vs a drone?', acceptedAnswer: { '@type': 'Answer', text: 'Both produce estimate-grade measurements suitable for ordering materials and quoting jobs. Drones can capture more surface detail up close; satellite is faster and cheaper for the area, pitch, and waste figures most estimates need.' } },
          ],
        })}</script>
      </Helmet>
      <Navbar />

      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-20 lg:py-24">
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
            Drone vs Satellite Roof Measurement
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl">
            Drones and satellites are both popular ways to measure a roof without climbing it — but they fit
            different jobs. Here's an honest comparison of cost, speed, equipment, licensing, and safety,
            plus when satellite measurement is the faster, cheaper choice for estimates.
          </p>
          <Button onClick={handleStart} className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-8 font-heading font-bold tracking-wide text-lg">
            Try Satellite Measurement Free <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-heading font-black text-3xl text-slate-900 mb-8 text-center">Satellite vs Drone vs Manual</h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-left">
            <thead className="bg-slate-50">
              <tr>
                <th className="p-4 font-heading font-bold text-slate-700">Factor</th>
                <th className="p-4 font-heading font-bold text-orange-600">Satellite (SquareCount)</th>
                <th className="p-4 font-heading font-bold text-slate-500">Drone</th>
                <th className="p-4 font-heading font-bold text-slate-500">Manual</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => (
                <tr key={i} className={i % 2 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="p-4 font-medium text-slate-700">{row[0]}</td>
                  <td className="p-4 text-slate-900 font-semibold">{row[1]}</td>
                  <td className="p-4 text-slate-500">{row[2]}</td>
                  <td className="p-4 text-slate-500">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-heading font-black text-3xl text-slate-900 mb-6">When to use each</h2>
          <div className="space-y-4 text-slate-700">
            <p><strong>Satellite</strong> — when you need a fast, low-cost estimate (area, pitch, waste, materials) without leaving the office. Best for quoting volume.</p>
            <p><strong>Drone</strong> — when you need detailed close-up imagery or a surface inspection (damage, debris), and have a Part 107 license and time to fly on-site.</p>
            <p><strong>Manual</strong> — a fallback when you're already on-site, but it's the slowest and carries fall risk.</p>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading font-black text-3xl mb-4">Measure roofs without a drone or a ladder</h2>
          <p className="text-slate-300 mb-8">Satellite roof measurements in ~60 seconds. 3 free reports/month — and every Contractor feature free for your first 7 days.</p>
          <Button onClick={handleStart} className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-10 font-heading font-bold tracking-wide text-lg">
            Start Your 7-Day Contractor Trial <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
